import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { refreshDatasetCache, refreshImageCache } from "../../services/adminService";
import { fetchPendingExperiments } from '../../services/moderationService';
import { getAllFeedback } from '../../services/feedbackService';
import { getAllUsers } from '../../services/adminUserService';
import { fetchDatasetStats } from '../../services/datasetService';
import AdminLayout from '../../components/admin/AdminLayout';
import '../../styles/admin-dashboard.css';
import * as metadataService from '../../services/metadataService';
import { useNotification } from '../../components/framework/NotificationContext';

const AdminDashboard = () => {
    // State for dashboard data
    const [cacheLoading, setCacheLoading] = useState({ dataset: false, contributors: false, images: false });
    const { success, error } = useNotification();
    const [data, setData] = useState({
        pendingExperiments: [],
        feedback: [],
        users: [],
        datasetStats: { views: 0, downloads: 0 },
        metadataStats: { polymers: 0, solvents: 0, morphologies: 0, units: 0 },
        loading: true,
        error: null
    });

    // Fetch dashboard data
    useEffect(() => {
        const loadDashboardData = async () => {
            try {
                // Fetch in parallel for speed
                const [pendingData, feedbackData, usersData, statsData, polymers, solvents, morphologies, units] = await Promise.all([
                    fetchPendingExperiments().catch(() => []),
                    getAllFeedback().catch(() => []),
                    getAllUsers().catch(() => []),
                    fetchDatasetStats().catch(() => ({ views: 0, downloads: 0 })),
                    metadataService.getAllPolymers().catch(() => []),
                    metadataService.getAllSolvents().catch(() => []),
                    metadataService.getAllMorphologies().catch(() => []),
                    metadataService.getAllUnits().catch(() => []),
                ]);

                setData({
                    pendingExperiments: Array.isArray(pendingData) ? pendingData : [],
                    feedback: Array.isArray(feedbackData) ? feedbackData : [],
                    users: Array.isArray(usersData) ? usersData : [],
                    datasetStats: statsData || { views: 0, downloads: 0 },
                    metadataStats: {
                        polymers: polymers.length,
                        solvents: solvents.length,
                        morphologies: morphologies.length,
                        units: units.length
                    },
                    loading: false,
                    error: null
                });
            } catch (error) {
                console.error("Failed to load dashboard stats:", error);
                setData(prev => ({ ...prev, loading: false, error: "Failed to load dashboard data." }));
            }
        };

        loadDashboardData();
    }, []);

    const handleRefreshAllCaches = async () => {
        setCacheLoading(prev => ({ ...prev, dataset: true, images: true }));
        try {
            // Since we unified versioning, one call refreshes everything (Data + Images)
            const message = await refreshDatasetCache();
            success(`Refresh successful: ${message}`);
        } catch (err) {
            console.error("Cache refresh error:", err);
            error(`Failed to refresh caches.\n${err.message || 'Unknown error occurred.'}`);
        } finally {
            setCacheLoading(prev => ({ ...prev, dataset: false, images: false }));
        }
    };

    // const handleRefreshContributorsCache = async () => {
    //     setCacheLoading(prev => ({ ...prev, contributors: true }));
    //     try {
    //         const message = await refreshContributorsCache();
    //         success(`Success: ${message}`);
    //     } catch (err) {
    //         error(`Failed to refresh contributors cache.\n${err.message}`);
    //     } finally {
    //         setCacheLoading(prev => ({ ...prev, contributors: false }));
    //     }
    // };

    // Helper to format date
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    // Helper to truncate text
    const truncate = (text, maxLength = 30) => {
        if (!text) return '';
        return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
    };

    // Helper to check if date is within 24 hours
    const isWithin24Hours = (dateString) => {
        if (!dateString) return false;
        const date = new Date(dateString);
        const now = new Date();
        const oneDayMs = 24 * 60 * 60 * 1000;
        return (now - date) <= oneDayMs;
    };

    // Get top 5 recent items from the last 24 hours
    const recentPending = data.pendingExperiments
        .filter(item => isWithin24Hours(item.timestamp))
        .slice(0, 5);
    const recentFeedback = data.feedback
        .filter(item => isWithin24Hours(item.createdAt))
        .slice(0, 5);
    const recentUsers = data.users
        .filter(item => isWithin24Hours(item.createdAt))
        .slice(0, 5);

    return (
        <AdminLayout
            title="Mission Control"
            subtitle="System overview and recent activity."
        >
            {/* Summary Statistics Row */}
            <div className="dashboard-summary-row">
                <div className="summary-card">
                    <div className="summary-icon"><i className="bi bi-hourglass-split"></i></div>
                    <div className="summary-info">
                        <span className="summary-label">Pending</span>
                        <span className="summary-value">{data.loading ? '-' : data.pendingExperiments.length}</span>
                    </div>
                </div>
                <div className="summary-card">
                    <div className="summary-icon"><i className="bi bi-chat-text"></i></div>
                    <div className="summary-info">
                        <span className="summary-label">Feedback</span>
                        <span className="summary-value">{data.loading ? '-' : data.feedback.length}</span>
                    </div>
                </div>
                <div className="summary-card">
                    <div className="summary-icon"><i className="bi bi-people"></i></div>
                    <div className="summary-info">
                        <span className="summary-label">Users</span>
                        <span className="summary-value">{data.loading ? '-' : data.users.length}</span>
                    </div>
                </div>


                <div className="summary-card">
                    <div className="summary-icon"><i className="bi bi-eye"></i></div>
                    <div className="summary-info">
                        <span className="summary-label">Views</span>
                        <span className="summary-value">{data.loading ? '-' : data.datasetStats.views}</span>
                    </div>
                </div>

                <div className="summary-card">
                    <div className="summary-icon"><i className="bi bi-download"></i></div>
                    <div className="summary-info">
                        <span className="summary-label">Downloads</span>
                        <span className="summary-value">{data.loading ? '-' : data.datasetStats.downloads}</span>
                    </div>
                </div>
            </div>

            {/* Main Content Grid */}
            <div className="dashboard-grid-layout">

                {/* Recent Submissions */}
                <section className="dashboard-section">
                    <div className="section-header">
                        <h3><i className="bi bi-journal-text"></i> Recent Submissions</h3>
                        <Link to="/admin/manage-entries" className="view-all-link">View All</Link>
                    </div>
                    <div className="table-responsive">
                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>Status</th>
                                    <th>Date</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.loading ? (
                                    <tr><td colSpan="3" className="loading-cell">Loading...</td></tr>
                                ) : recentPending.length === 0 ? (
                                    <tr><td colSpan="3" className="empty-cell">No pending submissions.</td></tr>
                                ) : (
                                    recentPending.map(item => (
                                        <tr key={item.recordId}>
                                            <td className="mono-font">-</td>
                                            <td><span className="badge badge-pending">Pending</span></td>
                                            <td className="mono-font">{formatDate(item.timestamp)}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </section>

                {/* Recent Feedback */}
                <section className="dashboard-section">
                    <div className="section-header">
                        <h3><i className="bi bi-envelope"></i> Recent Feedback</h3>
                        <Link to="/admin/feedback" className="view-all-link">View All</Link>
                    </div>
                    <div className="table-responsive">
                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Subject</th>
                                    <th>Category</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.loading ? (
                                    <tr><td colSpan="3" className="loading-cell">Loading...</td></tr>
                                ) : recentFeedback.length === 0 ? (
                                    <tr><td colSpan="3" className="empty-cell">No messages.</td></tr>
                                ) : (
                                    recentFeedback.map((item, index) => (
                                        <tr key={index}>
                                            <td>{item.name || 'Anonymous'}</td>
                                            <td title={item.subject}>{truncate(item.subject, 20)}</td>
                                            <td><span className="badge badge-info">{item.category}</span></td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </section>

                {/* Recent Users - Full Width or separate if needed */}
                <section className="dashboard-section full-width">
                    <div className="section-header">
                        <h3><i className="bi bi-person-plus"></i> Newly Registered Users</h3>
                        <Link to="/admin/users" className="view-all-link">Manage Users</Link>
                    </div>
                    <div className="table-responsive">
                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Email</th>
                                    <th>Role</th>
                                    <th>Joined</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.loading ? (
                                    <tr><td colSpan="4" className="loading-cell">Loading...</td></tr>
                                ) : recentUsers.length === 0 ? (
                                    <tr><td colSpan="4" className="empty-cell">No users found.</td></tr>
                                ) : (
                                    recentUsers.map(user => (
                                        <tr key={user.id}>
                                            <td>{user.name}</td>
                                            <td>{user.email}</td>
                                            <td>
                                                <span className={`badge ${user.role === 'ADMIN' ? 'badge-admin' : 'badge-user'}`}>
                                                    {user.role}
                                                </span>
                                            </td>
                                            <td className="mono-font">{formatDate(user.createdAt)}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </section>

                {/* System Actions */}
                <section className="dashboard-section full-width">
                    <div className="section-header">
                        <h3><i className="bi bi-gear"></i> System Maintenance</h3>
                    </div>
                    <div className="action-buttons">
                        <button
                            className="btn-scientific btn-outline"
                            onClick={handleRefreshAllCaches}
                            disabled={cacheLoading.dataset || cacheLoading.images}
                        >
                            {(cacheLoading.dataset || cacheLoading.images) ? (
                                <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>Refreshing...</>
                            ) : (
                                <><i className="bi bi-arrow-repeat"></i> Refresh All Caches (Data & Images)</>
                            )}
                        </button>
                        <Link to="/admin/email/send" className="btn-scientific btn-outline text-decoration-none">
                            <i className="bi bi-envelope-plus"></i> Send Email
                        </Link>
                        {/* <button
                            className="btn-scientific btn-outline"
                            onClick={handleRefreshContributorsCache}
                            disabled={cacheLoading.contributors}
                        >
                            {cacheLoading.contributors ? (
                                <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>Refreshing...</>
                            ) : (
                                <><i className="bi bi-people"></i> Refresh Contributors Cache</>
                            )}
                        </button> */}
                    </div>
                </section>

                {/* Metadata Management */}
                <section className="dashboard-section full-width">
                    <div className="section-header">
                        <h3><i className="bi bi-tags"></i> Metadata Management</h3>
                        <Link to="/admin/metadata" className="view-all-link">Manage All</Link>
                    </div>
                    <div className="dashboard-summary-row" style={{ marginTop: '1.5rem' }}>
                        <div className="summary-card small">
                            <div className="summary-info">
                                <span className="summary-label">Polymers</span>
                                <span className="summary-value">{data.loading ? '-' : data.metadataStats.polymers}</span>
                            </div>
                        </div>
                        <div className="summary-card small">
                            <div className="summary-info">
                                <span className="summary-label">Solvents</span>
                                <span className="summary-value">{data.loading ? '-' : data.metadataStats.solvents}</span>
                            </div>
                        </div>
                        <div className="summary-card small">
                            <div className="summary-info">
                                <span className="summary-label">Morphologies</span>
                                <span className="summary-value">{data.loading ? '-' : data.metadataStats.morphologies}</span>
                            </div>
                        </div>
                        <div className="summary-card small">
                            <div className="summary-info">
                                <span className="summary-label">Units</span>
                                <span className="summary-value">{data.loading ? '-' : data.metadataStats.units}</span>
                            </div>
                        </div>
                    </div>
                </section>
            </div >
        </AdminLayout >
    );
};

export default AdminDashboard;